import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Sheet } from './Sheet'

export interface ConfirmOptionen {
  titel: string
  text: string
  bestaetigen?: string
  abbrechen?: string
  gefahr?: boolean
}

type Bestaetigen = (optionen: ConfirmOptionen) => Promise<boolean>

const ConfirmContext = createContext<Bestaetigen>(() => Promise.resolve(false))

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [optionen, setOptionen] = useState<ConfirmOptionen | null>(null)
  const antwort = useRef<((wert: boolean) => void) | null>(null)

  const bestaetigen = useCallback<Bestaetigen>((naechste) => {
    antwort.current?.(false)
    setOptionen(naechste)
    return new Promise<boolean>((resolve) => {
      antwort.current = resolve
    })
  }, [])

  const abschliessen = (wert: boolean) => {
    const resolve = antwort.current
    antwort.current = null
    setOptionen(null)
    resolve?.(wert)
  }

  return (
    <ConfirmContext.Provider value={bestaetigen}>
      {children}
      <Sheet
        offen={optionen !== null}
        titel={optionen?.titel ?? 'Bestätigen'}
        onSchliessen={() => abschliessen(false)}
      >
        {optionen ? (
          <div className="stack-4">
            <p>{optionen.text}</p>
            <div className="blatt__fuss-inline">
              <Button variant="text" onClick={() => abschliessen(false)} autoFocus>
                {optionen.abbrechen ?? 'Abbrechen'}
              </Button>
              <Button
                className={optionen.gefahr ? 'btn--gefahr' : undefined}
                onClick={() => abschliessen(true)}
              >
                {optionen.bestaetigen ?? 'Bestätigen'}
              </Button>
            </div>
          </div>
        ) : null}
      </Sheet>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): Bestaetigen {
  return useContext(ConfirmContext)
}
